import { Alert, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Sms } from "iconsax-react";
import type { ReactNode } from "react";
import { useState } from "react";
import type { Control, FieldErrors, UseFormHandleSubmit, UseFormRegister } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  AuthDivider,
  AuthFormLayout,
  AuthOutlineButton,
  AuthTabBar,
  FloatingTextField,
  Link,
  SefanoButton,
  SefanoCheckbox,
} from "@/components";
import { TermsDialog } from "@/components/Organisms/TermsDialog";
import type { LoginFormValues, RegisterFormValues } from "@/lib/validation/auth.schemas";
import * as styles from "./Credential.template.styles";

type LoginCredentialTemplateProps = {
  register: UseFormRegister<LoginFormValues>;
  control: Control<LoginFormValues>;
  handleSubmit: UseFormHandleSubmit<LoginFormValues>;
  onSubmit: (data: LoginFormValues) => void;
  errors: FieldErrors<LoginFormValues>;
  isPending: boolean;
  serverError?: string | null;
};

type RegisterCredentialTemplateProps = {
  register: UseFormRegister<RegisterFormValues>;
  control: Control<RegisterFormValues>;
  handleSubmit: UseFormHandleSubmit<RegisterFormValues>;
  onSubmit: (data: RegisterFormValues) => void;
  errors: FieldErrors<RegisterFormValues>;
  isPending: boolean;
  serverError?: string | null;
};

function CredentialShell({
  mode,
  children,
  footerText,
  footerAction,
  footerTo,
}: {
  mode: "login" | "register";
  children: ReactNode;
  footerText: string;
  footerAction: string;
  footerTo: "/login" | "/register";
}) {
  const { t } = useTranslation("auth");
  const theme = useTheme();
  const isLogin = mode === "login";

  return (
    <AuthFormLayout
      title={isLogin ? t("login_panel_title") : t("register_panel_title")}
      subtitle={isLogin ? t("login_panel_subtitle") : t("register_panel_subtitle")}
    >
      <AuthTabBar activeTab={mode} />
      {children}
      <AuthDivider />
      <AuthOutlineButton
        to="/otp"
        icon={<Sms size={20} variant="Linear" color={theme.palette.primary.main} />}
      >
        {t("login_with_otp")}
      </AuthOutlineButton>
      <Stack sx={styles.footerLinkRow}>
        <Typography sx={styles.footerText}>{footerText}</Typography>
        <Link to={footerTo}>
          <Typography sx={styles.footerAction}>{footerAction}</Typography>
        </Link>
      </Stack>
    </AuthFormLayout>
  );
}

export function LoginCredentialTemplate({
  register,
  control,
  handleSubmit,
  onSubmit,
  errors,
  isPending,
  serverError,
}: LoginCredentialTemplateProps) {
  const { t } = useTranslation("auth");

  return (
    <CredentialShell
      mode="login"
      footerText={t("dont_have_acccount")}
      footerAction={t("register_now")}
      footerTo="/register"
    >
      <Stack component="form" sx={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FloatingTextField
          label={t("phone")}
          placeholder={t("please_insert_phone")}
          type="tel"
          autoComplete="tel"
          inputMode="numeric"
          dir="ltr"
          error={Boolean(errors.username)}
          helperText={errors.username?.message}
          {...register("username")}
        />

        <FloatingTextField
          label={t("password")}
          placeholder="••••••••"
          type="password"
          autoComplete="current-password"
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          {...register("password")}
        />

        <Stack sx={styles.optionsRow}>
          <Controller
            name="rememberMe"
            control={control}
            render={({ field }) => (
              <SefanoCheckbox
                label={t("remember_me")}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Typography component="button" type="button" sx={styles.forgotPasswordLink}>
            {t("forgot_password")}
          </Typography>
        </Stack>

        {serverError ? (
          <Alert severity="error" role="alert" sx={styles.serverError}>
            {serverError}
          </Alert>
        ) : null}

        <Stack sx={styles.submitWrap}>
          <SefanoButton type="submit" fullWidth disabled={isPending}>
            {isPending ? t("loading") : t("login")}
          </SefanoButton>
        </Stack>
      </Stack>
    </CredentialShell>
  );
}

export function RegisterCredentialTemplate({
  register,
  control,
  handleSubmit,
  onSubmit,
  errors,
  isPending,
  serverError,
}: RegisterCredentialTemplateProps) {
  const { t } = useTranslation("auth");
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <CredentialShell
      mode="register"
      footerText={t("already_have_acccount")}
      footerAction={t("login_now")}
      footerTo="/login"
    >
      <Stack component="form" sx={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FloatingTextField
          label={t("user_name")}
          placeholder={t("please_insert_username")}
          autoComplete="username"
          error={Boolean(errors.username)}
          helperText={errors.username?.message}
          {...register("username")}
        />

        <FloatingTextField
          label={t("phone")}
          placeholder={t("please_insert_phone")}
          type="tel"
          autoComplete="tel"
          inputMode="numeric"
          dir="ltr"
          error={Boolean(errors.phone)}
          helperText={errors.phone?.message}
          {...register("phone")}
        />

        <FloatingTextField
          label={t("password")}
          placeholder="••••••••"
          type="password"
          autoComplete="new-password"
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          {...register("password")}
        />

        <FloatingTextField
          label={t("password_repeat")}
          placeholder="••••••••"
          type="password"
          autoComplete="new-password"
          error={Boolean(errors.repeate_password)}
          helperText={errors.repeate_password?.message}
          {...register("repeate_password")}
        />

        <Stack sx={styles.termsRow}>
          <Controller
            name="acceptTerms"
            control={control}
            render={({ field }) => (
              <SefanoCheckbox
                variant="circle"
                label={t("accept_terms")}
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Typography
            component="button"
            type="button"
            sx={styles.viewTermsLink}
            onClick={() => setTermsOpen(true)}
          >
            {t("view_terms")}
          </Typography>
        </Stack>

        {errors.acceptTerms ? (
          <Typography variant="caption" color="error" sx={styles.termsError}>
            {errors.acceptTerms.message}
          </Typography>
        ) : null}

        {serverError ? (
          <Alert severity="error" role="alert" sx={styles.serverError}>
            {serverError}
          </Alert>
        ) : null}

        <Stack sx={styles.submitWrap}>
          <SefanoButton type="submit" fullWidth disabled={isPending}>
            {isPending ? t("loading") : t("register")}
          </SefanoButton>
        </Stack>
      </Stack>

      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
    </CredentialShell>
  );
}
